import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { 
  Globe, 
  Check, 
  ArrowRight, 
  Clock, 
  Users, 
  Award, 
  Sparkles,
  AlertCircle,
  Building2,
  Wrench,
  Store,
  Home as HomeIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const slotsRemaining = 2; // Update this as slots fill

const eligibleBusinesses = [
  { icon: Wrench, name: "Tradespeople", examples: "Builders, Electricians, Plumbers, Roofers" },
  { icon: Store, name: "Local Shops", examples: "Retailers, Salons, Barbers, Cafes" },
  { icon: Building2, name: "Service Businesses", examples: "Accountants, Consultants, Agencies" },
  { icon: HomeIcon, name: "Home-Based", examples: "Freelancers, Tutors, Therapists" },
];

const portfolioExamples = [
  {
    title: "Local Plumbing Service",
    description: "Clean, professional website with booking integration",
    image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&q=80"
  },
  {
    title: "Hair & Beauty Salon",
    description: "Stylish design showcasing services and gallery",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&q=80"
  },
  {
    title: "Building Contractor",
    description: "Portfolio-focused site with project showcases",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80"
  }
];

const whyUs = [
  { title: "15+ Years Experience", description: "We've been building websites for small businesses since 2009." },
  { title: "78+ Projects Delivered", description: "Proven track record of helping local businesses grow online." },
  { title: "Mobile-First Design", description: "Every site looks perfect on phones, tablets, and desktops." },
  { title: "SEO Ready", description: "Built to be found on Google from day one." },
];

export default function FreeWebsite() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telephone: "",
    businessName: "",
    businessType: "",
    hasWebsite: "",
    operatingTime: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        telephone: formData.telephone,
        message: `[FREE WEBSITE APPLICATION]
        
Business Name: ${formData.businessName}
Business Type: ${formData.businessType}
Has Existing Website: ${formData.hasWebsite}
Time Operating: ${formData.operatingTime}

Message: ${formData.message}`
      });
      setIsSubmitted(true);
      toast.success("Application submitted! We'll review and be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="free-website-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Free Website for Southport Businesses | Limited Availability | Barton</title>
        <meta name="description" content="Southport business owner? Apply for a FREE professional website worth £299. Only 3 spots available. Supporting local businesses in our community." />
        <meta property="og:title" content="Free Website for Southport Businesses | Barton" />
        <meta property="og:description" content="Southport business owner? Apply for a FREE professional website worth £299. Only 3 spots available!" />
        <meta property="og:url" content="https://barton.uk/free-website" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="Free Website for Southport Businesses | Barton" />
        <meta name="twitter:description" content="Southport business owner? Apply for a FREE professional website worth £299!" />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Urgency Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 border border-pink-500/30 text-pink-400 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Only {slotsRemaining} Free Websites Available
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Southport Businesses:{" "}
            <span className="text-gradient">Get a Free Website</span>
          </h1>
          
          <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto">
            We're giving away 3 free websites to local Southport businesses.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-8 py-6 text-lg"
              onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Apply Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="rounded-full px-8 py-6 text-lg border-white/20 text-white hover:bg-white/10 bg-transparent"
              onClick={() => document.getElementById('eligibility')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Check Eligibility
            </Button>
          </div>
        </div>
      </section>

      {/* Slots Counter */}
      <section className="bg-gradient-to-r from-purple-500/10 via-transparent to-pink-500/10 py-8 border-y border-white/10">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className={`w-10 h-10 rounded-full border-2 border-black flex items-center justify-center text-sm font-bold ${
                      i <= slotsRemaining ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' : 'bg-white/20 text-white/40'
                    }`}
                  >
                    {i}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">{slotsRemaining} of 3 Slots Remaining</p>
                <p className="text-white/50 text-sm">Apply before they're gone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section id="eligibility" className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
              Who Is This For?
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              This offer is exclusively for established Southport businesses looking to get online or refresh their existing website
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {eligibleBusinesses.map((business, index) => (
              <div
                key={index}
                className="bg-[#111111] border border-white/10 rounded-2xl p-6 text-center hover:border-pink-500/50 transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500/10 flex items-center justify-center mx-auto mb-4">
                  <business.icon className="w-7 h-7 text-pink-400" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2 text-white">{business.name}</h3>
                <p className="text-white/50 text-sm">{business.examples}</p>
              </div>
            ))}
          </div>

          {/* Eligibility Criteria */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-bold mb-6 text-white flex items-center gap-2">
                <Check className="w-6 h-6 text-pink-400" />
                Eligibility Criteria
              </h3>
              
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-white/80">Your business must be <strong className="text-white">based in or around Southport</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-white/80">Your business must have been operating for <strong className="text-white">at least 3-6 months</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-white/80">You can apply whether you <strong className="text-white">have an existing website or not</strong> – revamps and refreshes welcome!</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-white/80">Perfect for <strong className="text-white">local Southport service businesses</strong> – tradespeople, shops, home-based businesses</span>
                </li>
              </ul>

              <div className="border-t border-white/10 pt-6">
                <h4 className="font-semibold mb-4 text-white/60 text-sm uppercase tracking-wider">Not Eligible:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <span className="text-white/60">New startups or people who don't yet have an operating business</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <span className="text-white/60">E-commerce stores (online shops with product catalogues)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
              Why Southport Businesses Trust Us
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              We've been helping small businesses succeed online for over 15 years
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {whyUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  {index === 0 && <Clock className="w-8 h-8 text-purple-400" />}
                  {index === 1 && <Award className="w-8 h-8 text-purple-400" />}
                  {index === 2 && <Globe className="w-8 h-8 text-purple-400" />}
                  {index === 3 && <Users className="w-8 h-8 text-purple-400" />}
                </div>
                <h3 className="font-heading text-lg font-bold mb-2 text-white">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Portfolio Preview */}
          <div className="grid md:grid-cols-3 gap-6">
            {portfolioExamples.map((example, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl">
                <img 
                  src={example.image} 
                  alt={example.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4">
                  <div>
                    <h4 className="font-semibold text-white">{example.title}</h4>
                    <p className="text-white/60 text-sm">{example.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                {slotsRemaining} Slots Remaining
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
                Apply for Your Free Website
              </h2>
              <p className="text-white/60 text-lg mb-2">
                Fill out the form below to apply for one of our free websites.
              </p>
              <p className="text-white/50 text-sm">
                Successful applicants will be announced at a later date. If your business is selected, you will be contacted within 48 hours.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-[#111111] border border-white/10 rounded-2xl p-8">
                <div className="space-y-6">
                  {/* Personal Details */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@email.com"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="telephone" className="text-white">Phone Number *</Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        required
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="Your phone number"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="text-white">Business Name *</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        type="text"
                        required
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Your business name"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>
                  </div>

                  {/* Business Details */}
                  <div className="space-y-2">
                    <Label htmlFor="businessType" className="text-white">What type of business do you have? *</Label>
                    <Input
                      id="businessType"
                      name="businessType"
                      type="text"
                      required
                      value={formData.businessType}
                      onChange={handleChange}
                      placeholder="e.g., Plumber, Hair Salon, Accountant, etc."
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="hasWebsite" className="text-white">Do you have an existing website? *</Label>
                      <select
                        id="hasWebsite"
                        name="hasWebsite"
                        required
                        value={formData.hasWebsite}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md bg-black/50 border border-white/10 text-white focus:border-pink-500 focus:outline-none"
                      >
                        <option value="" className="bg-black">Select an option</option>
                        <option value="no" className="bg-black">No, I need a new website</option>
                        <option value="yes-revamp" className="bg-black">Yes, but I want it revamped</option>
                        <option value="yes-refresh" className="bg-black">Yes, but it needs a refresh</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="operatingTime" className="text-white">How long have you been operating? *</Label>
                      <select
                        id="operatingTime"
                        name="operatingTime"
                        required
                        value={formData.operatingTime}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md bg-black/50 border border-white/10 text-white focus:border-pink-500 focus:outline-none"
                      >
                        <option value="" className="bg-black">Select an option</option>
                        <option value="less-than-3" className="bg-black">Less than 3 months</option>
                        <option value="3-6-months" className="bg-black">3-6 months</option>
                        <option value="6-12-months" className="bg-black">6-12 months</option>
                        <option value="1-3-years" className="bg-black">1-3 years</option>
                        <option value="3-plus-years" className="bg-black">3+ years</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Tell us about your business *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What does your business do? What would you like your website to achieve?"
                      rows={4}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="free-website-submit"
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full py-6 text-lg"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  {/* Small Print */}
                  <p className="text-white/40 text-xs text-center">
                    By submitting this application, you agree to our terms. A hosting fee may apply after the initial 3-month period. 
                    This offer is limited to 3 businesses and excludes e-commerce stores.
                  </p>
                </div>
              </form>
            ) : (
              <div className="bg-[#111111] border border-pink-500/50 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-pink-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2 text-white">Application Received!</h3>
                <p className="text-white/60 mb-6">
                  Thank you for your application! We'll review it and get back to you within 48 hours.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/20 text-white hover:bg-white/10 bg-transparent"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Didn't Get Selected? */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4 text-white">
            Didn't Get a Free Website?
          </h2>
          <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
            Don't worry! We offer affordable website design services for all businesses. 
            Check out our web design packages and let's build something great together.
          </p>
          <Button
            asChild
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:bg-gradient-to-r from-blue-500 to-purple-500/90 text-white rounded-full px-8 py-6 text-lg"
          >
            <Link to="/services/web">
              View Our Web Design Services <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* E-commerce / Advanced */}
      <section className="bg-black py-12 border-t border-white/10">
        <div className="container-custom text-center">
          <p className="text-white/60 text-lg mb-4">
            Looking for a more advanced website or e-commerce store?
          </p>
          <Button
            asChild
            variant="outline"
            className="rounded-full px-6 py-4 border-white/20 text-white hover:bg-white/10 bg-transparent"
          >
            <Link to="/services/web">
              Explore Our Web Design Services <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
